import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Download,
  People,
  CreditCard,
  Storage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Close as CloseIcon,
  PictureAsPdf,
  Description,
  TableChart,
  Code,
  Image,
  VideoFile,
  Audiotrack,
  Archive,
  InsertDriveFile,
  Web,
  DataObject,
  Send,
  Workspaces,
  ArrowUpward,
  ArrowDownward,
  Chat as ChatIcon,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { ADMIN_HUB_TABS, ADMIN_HUB_URL_PARAMS, API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, SOCKET_KEYS, PLATFORM_NAME, DISABLE_SUBSCRIPTION, getAdminHubTabIndex, getAdminHubUserView } from '../helpers/constants';
import { LanguageConstants } from '../helpers/LanguageConstants';
import socketService from '../services/socketService';
import channelService, { Workspace } from '../services/channelService';
import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableRowStyles, 
  getTableCellStyles 
} from '../styles/tableStyles';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { modalStyles } from '../styles/modalStyles';
import AddLogDataSourceModal from '../components/AddLogDataSourceModal';
import AddUserModal from '../components/AddUserModal';
import ViewInvoiceModal from '../components/ViewInvoiceModal';

import PlansCard from '../components/PlansCard';
import PlansModal from '../components/PlansModal';
import { getCurrentSubscription, SubscriptionData, fetchCompanySubscriptionDetails, InvoiceDetail, UsageStatisticsFromApi, downloadInvoice, nonNegativeUsageStat } from '../services/subscriptionService';
import { userService, User, UsersResponse, PendingInvitation, AccessRequest } from '../services/userService';
import { usePlanUpgrade } from '../hooks/usePlanUpgrade';
import { Plan, fetchPlans } from '../services/companyService';
import { capitalizeFirstLetter } from '../helpers/stringUtils';
import { formatDisplayDateTime, formatDisplayDate } from '../helpers/dateUtils';
import { getDataSourceIcon } from '../helpers/dataSourceUtils';
import { getZohoSubscriptionId } from '../helpers/storageUtils';
import { getFetchErrorMessage } from '../helpers/errorUtils';
import { formatSubscriptionStorageUsedOfLimit } from '../helpers/subscriptionUsageFormat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatService } from '../components/ChatService';

// Helper component for expandable text
const ExpandableText: React.FC<{ text: string; limit?: number }> = ({ text, limit = 50 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;
  if (text.length <= limit) return <Typography variant="body2">{text}</Typography>;

  return (
    <Box>
      <Typography 
        variant="body2" 
        component="span" 
        sx={{ 
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap' 
        }}
      >
        {isExpanded ? text : text.slice(0, limit)}
      </Typography>
      {!isExpanded ? (
        <Typography
          variant="body2"
          component="span"
          color="primary"
          sx={{ 
            cursor: 'pointer', 
            fontWeight: 'bold',
            ml: 0.5
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
        >
          ...
        </Typography>
      ) : (
         <Typography
          variant="caption"
          color="primary"
          sx={{ 
            cursor: 'pointer', 
            fontWeight: 500,
            display: 'block',
            mt: 0.5
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(false);
          }}
        >
          Show Less
        </Typography>
      )}
    </Box>
  );
};

// Datasource API interfaces
interface DataSource {
  id: string;
  name: string;
  description?: string;
  filename?: string;
  storage_type?: string;
  provider?: string;
  app_type?: string;
  config?: any;
  datasource_metadata?: any;
  giggso_vault_id?: string;
  file_size?: number;
  file_type?: string;
  file_url?: string;
  channel?: {
    id: string;
    name: string;
    description?: string;
    workspace_id: string;
    workspace_name?: string;
    company_id: string;
    is_active: boolean;
    is_public: boolean;
    ai_enabled: boolean;
    channel_settings?: any;
    message_count: number;
    last_message_at?: string;
    created_at: string;
    updated_at: string;
  };
  added_by_user?: {
    user_id: string;
    name: string;
    email_id: string;
    company_id: string;
    role: string;
    created_datetime: string;
    updated_datetime: string;
  };
  is_active: boolean;
  is_connected: boolean;
  is_processed: boolean;
  processing_status: string;
  error_message?: string;
  last_processed_at?: string;
  processing_time?: number;
  record_count: number;
  created_at: string;
  updated_at: string;
}

interface DataSourcesResponse {
  datasources: DataSource[];
  total: number;
  page: number;
  size: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}



const AdminHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabIndex = getAdminHubTabIndex(
    searchParams.get(ADMIN_HUB_URL_PARAMS.TAB),
    DISABLE_SUBSCRIPTION,
  );
  const initialUserView = getAdminHubUserView(
    searchParams.get(ADMIN_HUB_URL_PARAMS.TAB),
    searchParams.get(ADMIN_HUB_URL_PARAMS.ACTIVE),
  );
  const [tabValue, setTabValue] = useState(initialTabIndex ?? 0);
  // When subscription is disabled, tab 3 is Chat Service; when enabled, tab 3 is Subscription and tab 4 is Chat Service
  const chatServiceTabIndex = DISABLE_SUBSCRIPTION ? 3 : 4;
  // When subscription is disabled and user lands on old subscription index (3), do not reset — 3 is now Chat Service
  const [openModal, setOpenModal] = useState(false);

  // State for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [datasourceToDelete, setDatasourceToDelete] = useState<DataSource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for multi-select + bulk delete of data sources (checkbox column)
  const [selectedDatasourceIds, setSelectedDatasourceIds] = useState<Set<string>>(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // True while the "select all" checkbox is fetching every data source id across all
  // pages (not just the currently displayed page) — see handleToggleSelectAllDatasources.
  const [isSelectingAllDatasources, setIsSelectingAllDatasources] = useState(false);

  // State for delete user confirmation modal
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  // State for edit user modal
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    role: '',
    is_active: true
  });

  // State for add user modal
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [plansModalOpen, setPlansModalOpen] = useState(false);

  // State for subscription data
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // State for Workspaces
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspacesTotal, setWorkspacesTotal] = useState(0);
  const [workspacesPage, setWorkspacesPage] = useState(1);
  const [workspacesPageSize, setWorkspacesPageSize] = useState(10);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);
  const [workspacesSearch, setWorkspacesSearch] = useState('');
  const [workspacesSortBy, setWorkspacesSortBy] = useState('name');
  const [workspacesSortOrder, setWorkspacesSortOrder] = useState<'asc' | 'desc'>('asc');

  // State for edit/delete workspace
  const [editWorkspaceModalOpen, setEditWorkspaceModalOpen] = useState(false);
  const [workspaceToEdit, setWorkspaceToEdit] = useState<Workspace | null>(null);
  const [editWorkspaceFormData, setEditWorkspaceFormData] = useState({ name: '', description: '' });
  const [isUpdatingWorkspace, setIsUpdatingWorkspace] = useState(false);

  const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(null);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);

  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  // State for Recent Invoices (from fetchCompanySubscriptionDetails API)
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetail[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoicesSearch, setInvoicesSearch] = useState('');
  // State for view invoice modal (click on invoice number)
  const [viewInvoiceModalOpen, setViewInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  const [invoiceDownloadingId, setInvoiceDownloadingId] = useState<string | null>(null);

  // Parsed usage statistics from fetchCompanySubscriptionDetails (used = limit - available)
  const [usageStats, setUsageStats] = useState<{
    channels: { used: number; limit: number };
    messages: { used: number; limit: number };
    storage: { usedBytes: number; limitBytes: number };
  } | null>(null);

  // State for datasources API
  const [datasources, setDatasources] = useState<DataSource[]>([]);
  const [datasourcesLoading, setDatasourcesLoading] = useState<boolean>(false);
  const [datasourcesError, setDatasourcesError] = useState<string | null>(null);
  const [datasourcesPage, setDatasourcesPage] = useState<number>(1);
  const [datasourcesTotal, setDatasourcesTotal] = useState<number>(0);
  // Embedding status per datasource id, sourced from the same
  // GET /datasources/embedding-status/{channel_id} API the channel-level
  // Data Sources panel uses — keyed by datasource id so the table can
  // reflect the true embedding state instead of the raw processing fields.
  const [embeddingStatusMap, setEmbeddingStatusMap] = useState<Record<string, { status: number; error_message?: string | null }>>({});
  const [datasourcesSearch, setDatasourcesSearch] = useState<string>('');
  const [pageSize] = useState<number>(10);

  // State for users API
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersPage, setUsersPage] = useState<number>(1);
  const [usersTotal, setUsersTotal] = useState<number>(0);
  const [usersSearch, setUsersSearch] = useState<string>('');
  const [usersRoleFilter, setUsersRoleFilter] = useState<string>('');
  const [usersStatusFilter, setUsersStatusFilter] = useState<string>('');

  // Click-to-filter for the combined Users/Invitations/Requests table's summary cards.
  // 'all' (default) shows every category together; clicking a card narrows the table to
  // just that Type. Preserves the AdminHub deep-link (PR #615): a URL landing directly on
  // Access Requests starts pre-filtered to 'requests' instead of showing everything.
  const [typeFilter, setTypeFilter] = useState<'all' | 'onboarded' | 'pending' | 'requests'>(
    initialUserView === 'requests' ? 'requests' : 'all'
  );

  // State for Pending Users
  const [pendingUsers, setPendingUsers] = useState<PendingInvitation[]>([]);
  const [pendingUsersTotal, setPendingUsersTotal] = useState<number>(0);
  const [pendingUsersLoading, setPendingUsersLoading] = useState<boolean>(false);
  const [pendingUsersError, setPendingUsersError] = useState<string | null>(null);
  const [pendingUsersPage, setPendingUsersPage] = useState<number>(1);
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [accessRequestsTotal, setAccessRequestsTotal] = useState<number>(0);
  const [accessRequestsPendingTotal, setAccessRequestsPendingTotal] = useState<number>(0);
  const [accessRequestsLoading, setAccessRequestsLoading] = useState<boolean>(false);
  const [accessRequestsError, setAccessRequestsError] = useState<string | null>(null);
  const [accessRequestsPage, setAccessRequestsPage] = useState<number>(1);
  const [accessRequestsStatusFilter, setAccessRequestsStatusFilter] = useState<string>('pending');
  // Request ids currently being approved/rejected — disables that row's buttons for the
  // duration of the call (prevents a double-click firing two concurrent approve/reject
  // calls for the same row) and lets the failure path re-sync from the server instead of
  // leaving a stale, still-clickable row when the backend's atomic status check rejects it
  // (e.g. two admins act on the same request, or a double-click races itself).
  const [processingAccessRequestIds, setProcessingAccessRequestIds] = useState<Set<string>>(new Set());
  // Role selected per pending request row (defaults to 'user', same as the
  // backend's default when no role is sent) — keyed by request id so each
  // row's dropdown is independent.
  const [accessRequestRoleSelections, setAccessRequestRoleSelections] = useState<Record<string, 'admin' | 'user'>>({});

  // State for delete pending invitation modal
  const [deletePendingInvitationModalOpen, setDeletePendingInvitationModalOpen] = useState(false);
  const [pendingInvitationToDelete, setPendingInvitationToDelete] = useState<PendingInvitation | null>(null);
  const [isDeletingPendingInvitation, setIsDeletingPendingInvitation] = useState(false);


  const { isDarkMode } = useTheme();
  const { user, markSessionExpired } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const { handlePlanSelect: handlePlanUpgrade } = usePlanUpgrade({
    currentPlanName: subscriptionData?.planName,
    onError: (errorMsg) => {
      alert(`Failed to create payment page: ${errorMsg}. Please try again.`);
      setPlansModalOpen(true); // Reopen modal on error
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Deep links such as /adminHub?tab=user-management should open the requested tab.
  // Subscription links may also open the Manage Plan dialog.
  useEffect(() => {
    const tab = searchParams.get(ADMIN_HUB_URL_PARAMS.TAB);
    const activeView = searchParams.get(ADMIN_HUB_URL_PARAMS.ACTIVE);
    const managePlanFlag = searchParams.get(ADMIN_HUB_URL_PARAMS.MANAGE_PLAN);
    const requestedTabIndex = getAdminHubTabIndex(tab, DISABLE_SUBSCRIPTION);
    if (requestedTabIndex === null) return;

    setTabValue(requestedTabIndex);
    // Same 'requests'-only special-case as the initial typeFilter state below —
    // preserves the Access Requests deep link (PR #615) when navigated to at runtime.
    setTypeFilter(getAdminHubUserView(tab, activeView) === 'requests' ? 'requests' : 'all');
    const shouldOpenManagePlan =
      !DISABLE_SUBSCRIPTION &&
      tab === ADMIN_HUB_TABS.SUBSCRIPTION_MANAGEMENT.value &&
      (managePlanFlag === '1' || managePlanFlag?.toLowerCase() === 'true');
    if (shouldOpenManagePlan) {
      setPlansModalOpen(true);
    }

    const next = new URLSearchParams(searchParams);
    next.delete(ADMIN_HUB_URL_PARAMS.TAB);
    next.delete(ADMIN_HUB_URL_PARAMS.ACTIVE);
    next.delete(ADMIN_HUB_URL_PARAMS.MANAGE_PLAN);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    // Refresh datasources when modal is closed so newly added ones are visible
    if (tabValue === 0) {
      setTimeout(() => fetchDatasources(1, pageSize, datasourcesSearch), 200);
    }
  };

  const handleDataSourceAdded = () => {
    if (tabValue === 0) {
      fetchDatasources(1, pageSize, datasourcesSearch);
    }
  };

  // Delete handlers
  const handleDeleteClick = (datasource: DataSource) => {
    setDatasourceToDelete(datasource);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDatasourceToDelete(null);
    setIsDeleting(false);
  };

  // Multi-select handlers for the data sources table's checkbox column
  const toggleDatasourceSelected = (id: string) => {
    setSelectedDatasourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // "Select all" spans every data source across every page (matching the current search),
  // not just the currently displayed page — fetches every id directly (page=1, size=total)
  // without touching the paginated `datasources` state the table renders from.
  const handleToggleSelectAllDatasources = async () => {
    const allSelected = datasourcesTotal > 0 && selectedDatasourceIds.size === datasourcesTotal;
    if (allSelected) {
      setSelectedDatasourceIds(new Set());
      return;
    }
    if (datasourcesTotal === 0) return;

    try {
      setIsSelectingAllDatasources(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const params = new URLSearchParams({
        page: '1',
        size: String(datasourcesTotal),
      });
      if (datasourcesSearch && datasourcesSearch.trim()) {
        params.append('search', datasourcesSearch.trim());
      }

      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: DataSourcesResponse = await response.json();
      setSelectedDatasourceIds(new Set(data.datasources.map((d) => d.id)));
    } catch (err) {
      toast.showError(`Failed to select all ${LanguageConstants.datasource_s.toLowerCase()}.`);
    } finally {
      setIsSelectingAllDatasources(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedDatasourceIds.size === 0) return;
    setBulkDeleteModalOpen(true);
  };

  const handleBulkDeleteModalClose = () => {
    setBulkDeleteModalOpen(false);
    setIsBulkDeleting(false);
  };

  // Reuses the same single-item DELETE endpoint as handleDeleteConfirm (no bulk-delete
  // API exists) — fires one request per selected id and reports how many failed.
  const handleBulkDeleteConfirm = async () => {
    if (selectedDatasourceIds.size === 0) return;
    const ids = Array.from(selectedDatasourceIds);

    try {
      setIsBulkDeleting(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

      const results = await Promise.allSettled(
        ids.map((id) =>
          fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.DELETE(id)}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
          }).then((res) => {
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          })
        )
      );

      const failedCount = results.filter((r) => r.status === 'rejected').length;

      setBulkDeleteModalOpen(false);
      setSelectedDatasourceIds(new Set());
      fetchDatasources(datasourcesPage, pageSize, datasourcesSearch);

      if (failedCount > 0) {
        toast.showError(`${failedCount} of ${ids.length} data source(s) could not be deleted.`);
      } else {
        toast.showSuccess(`${ids.length} data source(s) deleted successfully.`);
      }
    } catch (err) {
      toast.showError('Failed to delete selected data sources.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Delete user handlers
  const handleDeleteUserClick = (user: User) => {
    setUserToDelete(user);
    setDeleteUserModalOpen(true);
  };

  const handleDeleteUserModalClose = () => {
    setDeleteUserModalOpen(false);
    setUserToDelete(null);
    setIsDeletingUser(false);
  };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;

    try {
      setIsDeletingUser(true);
      
      // Get company ID from localStorage
      let companyId: string;
      try {
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          companyId = userData.company_id;
        } else {
          throw new Error('No user data found in localStorage');
        }
      } catch (error) {
        // Error retrieving company ID from localStorage
        return;
      }

      if (!companyId) {
        // Company ID not found
        return;
      }

      // Call the delete user API
      await userService.deleteUser(userToDelete.id, companyId);
      
      // Remove the deleted user from local state
      setUsers(prevUsers => 
        prevUsers.filter(user => user.id !== userToDelete.id)
      );
      
      // Update total count
      setUsersTotal(prev => prev - 1);
      
      // Close modal and show success message
      handleDeleteUserModalClose();
      
      // Optionally show a success toast/notification here
      toast.showSuccess('User deleted successfully');
      
    } catch (err) {
      // Error deleting user
      toast.showError('Failed to delete user');
    } finally {
      setIsDeletingUser(false);
    }
  };

  // Edit user handlers
  const handleEditUserClick = (user: User) => {
    setUserToEdit(user);
    setEditFormData({
      role: user.role,
      is_active: user.is_active
    });
    setEditUserModalOpen(true);
  };

  const handleEditUserModalClose = () => {
    setEditUserModalOpen(false);
    setUserToEdit(null);
    setEditFormData({
      role: '',
      is_active: true
    });
    setIsUpdating(false);
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add user modal handlers
  const handleAddUserModalOpen = () => {
    setAddUserModalOpen(true);
  };

  const handleAddUserModalClose = () => {
    setAddUserModalOpen(false);
  };

  // Fetch subscription data
  const fetchSubscriptionData = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);

      // Fetch subscription data (includes both real subscription and mock usage data)
      const subscriptionResponse = await getCurrentSubscription();
      
      if (subscriptionResponse.success) {
        setSubscriptionData(subscriptionResponse.data);
      } else {
        throw new Error('Failed to fetch subscription data');
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setSubscriptionError(getFetchErrorMessage(error, 'Failed to fetch subscription data'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch workspaces
  const fetchWorkspaces = async (
    page: number = 1, 
    size: number = 10, 
    search: string = '', 
    sortBy: string = 'name', 
    sortOrder: 'asc' | 'desc' = 'asc'
  ) => {
    try {
      setWorkspacesLoading(true);
      setWorkspacesError(null);
      
      const response = await channelService.getWorkspaces(page, size, search, sortBy, sortOrder);
      
      setWorkspaces(response.tags);
      setWorkspacesTotal(response.total);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      setWorkspacesError('Failed to load workspaces. Please try again.');
    } finally {
      setWorkspacesLoading(false);
    }
  };

  // Workspace Edit Handlers
  const handleEditWorkspaceClick = (workspace: Workspace) => {
    setWorkspaceToEdit(workspace);
    setEditWorkspaceFormData({
      name: workspace.name,
      description: workspace.description || ''
    });
    setEditWorkspaceModalOpen(true);
  };

  const handleEditWorkspaceModalClose = () => {
    setEditWorkspaceModalOpen(false);
    setTimeout(() => {
      setWorkspaceToEdit(null);
      setEditWorkspaceFormData({ name: '', description: '' });
    }, 200);
  };

  const handleUpdateWorkspace = async () => {
    if (!workspaceToEdit) return;

    try {
      setIsUpdatingWorkspace(true);
      await channelService.updateWorkspace(workspaceToEdit.id, editWorkspaceFormData);
      
      // Update local state
      setWorkspaces(prev => prev.map(ws => 
        ws.id === workspaceToEdit.id 
          ? { ...ws, ...editWorkspaceFormData } 
          : ws
      ));
      
      handleEditWorkspaceModalClose();
    } catch (error) {
      console.error('Error updating workspace:', error);
      // setWorkspacesError('Failed to update workspace');
    } finally {
      setIsUpdatingWorkspace(false);
    }
  };

  // Workspace Delete Handlers
  const handleDeleteWorkspaceClick = (workspace: Workspace) => {
    setWorkspaceToDelete(workspace);
    setDeleteWorkspaceModalOpen(true);
  };

  const handleDeleteWorkspaceModalClose = () => {
    setDeleteWorkspaceModalOpen(false);
    setTimeout(() => {
      setWorkspaceToDelete(null);
    }, 200);
  };

  const handleDeleteWorkspaceConfirm = async () => {
    if (!workspaceToDelete) return;

    try {
      setIsDeletingWorkspace(true);
      await channelService.deleteWorkspace(workspaceToDelete.id);
      
      // Update local state
      setWorkspaces(prev => prev.filter(ws => ws.id !== workspaceToDelete.id));
      setWorkspacesTotal(prev => prev - 1);
      
      handleDeleteWorkspaceModalClose();
    } catch (error) {
      console.error('Error deleting workspace:', error);
      // setWorkspacesError('Failed to delete workspace');
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  // Fetch available plans
  const fetchAvailablePlans = async () => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      
      const plansResponse = await fetchPlans();
      if (plansResponse.success) {
        setAvailablePlans(plansResponse.data);
      } else {
        throw new Error('Failed to fetch plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlansError(getFetchErrorMessage(error, 'Failed to fetch plans'));
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle plan selection from plans modal
  const handlePlanSelect = async (planId: string) => {
    try {
      await handlePlanUpgrade(planId, availablePlans, () => setPlansModalOpen(false));
    } catch (error) {
      // Error handling is done in usePlanUpgrade hook
    }
  };



  // Helper function to get company ID from localStorage
  const getCompanyId = (): string => {
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        return userData.company_id || '';
      }
          } catch (error) {
        // Error retrieving company ID from localStorage
      }
    return '';
  };

  // Parse usageStatistics from API (limit = total, available = remaining → used = limit - available)
  const parseUsageStatistics = (raw: UsageStatisticsFromApi) => {
    const r = raw as unknown as Record<string, unknown>;
    const channelLimit = nonNegativeUsageStat(raw.channelLimit);
    const channelAvailable = nonNegativeUsageStat(raw.channelavailable ?? r.channelAvailable);
    const messageLimit = nonNegativeUsageStat(raw.messageLimit);
    const messageAvailable = nonNegativeUsageStat(raw.messageavailable ?? r.messageAvailable);
    const storageLimitBytes = nonNegativeUsageStat(raw.StorageLimit ?? r.storageLimit);
    const storageAvailableBytes = nonNegativeUsageStat(raw.Storageavailable ?? r.storageAvailable);
    return {
      channels: { used: Math.max(0, channelLimit - channelAvailable), limit: channelLimit },
      messages: { used: Math.max(0, messageLimit - messageAvailable), limit: messageLimit },
      storage: { usedBytes: Math.max(0, storageLimitBytes - storageAvailableBytes), limitBytes: storageLimitBytes },
    };
  };

  // Fetch invoice details and usage statistics (POST fetchCompanySubscriptionDetails)
  const fetchInvoiceDetails = async () => {
    try {
      setInvoicesLoading(true);
      setInvoicesError(null);
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }
      const response = await fetchCompanySubscriptionDetails(companyId);
      if (response.success && response.data) {
        if (response.data.invoiceDetails != null) {
          setInvoiceDetails(response.data.invoiceDetails);
        } else {
          setInvoiceDetails([]);
        }
        // Parse and store usage statistics from API for Usage Statistics card
        if (response.data.usageStatistics) {
          setUsageStats(parseUsageStatistics(response.data.usageStatistics));
        } else {
          setUsageStats(null);
        }
      } else {
        setInvoiceDetails([]);
        setUsageStats(null);
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch invoices');
        }
      }
    } catch (error) {
      setInvoiceDetails([]);
      setUsageStats(null);
      setInvoicesError(error instanceof Error ? error.message : 'Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!userToEdit) return;

    try {
      setIsUpdating(true);
      
      // Get company ID from localStorage
      let companyId: string;
      try {
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          companyId = userData.company_id;
        } else {
          throw new Error('No user data found in localStorage');
        }
      } catch (error) {
        // Error retrieving company ID from localStorage
        return;
      }

      if (!companyId) {
        // Company ID not found
        return;
      }

      // Call the update user API
      const response = await userService.updateUser(userToEdit.id, editFormData, companyId);
      
      // Update the local users state with the updated user
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userToEdit.id ? response : user
        )
      );
      
      // Close modal and show success message
      handleEditUserModalClose();
      
      // Optionally show a success toast/notification here
      // User updated successfully
      
    } catch (err) {
      // Error updating user
      // Optionally show an error toast/notification here
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!datasourceToDelete) return;

    try {
      setIsDeleting(true);
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.DELETE(datasourceToDelete.id)}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Close modal and refresh data
      handleDeleteModalClose();
      
      // Emit CHANNEL_METRICS_UPDATE event to refresh channel metrics
      if (datasourceToDelete?.channel?.id) {
        try {
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_METRICS_UPDATE,
            data: {
              channelId: datasourceToDelete.channel.id
            }
          };
          socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Refresh the current page with current search
      fetchDatasources(datasourcesPage, pageSize, datasourcesSearch);
      
    } catch (err) {
      let errorMessage = 'Failed to delete datasource';
      
      if (err instanceof Error) {
        if (err.message.includes('HTTP error! status:')) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : '';
          
          switch (statusCode) {
            case '401':
              markSessionExpired();
              return;
            case '403':
              errorMessage = 'You do not have permission to delete this datasource.';
              break;
            case '404':
              errorMessage = 'Datasource not found. It may have already been deleted.';
              break;
            case '409':
              errorMessage = 'Cannot delete datasource. It may be in use.';
              break;
            case '500':
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Delete failed (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      // Delete error
      // You can add a toast notification here if needed
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch datasources from API
  // Fetch real embedding status for the current page of datasources by calling the same
  // per-channel GET /datasources/embedding-status/{channel_id} API the Data Sources panel uses,
  // once per unique channel present on the page, then index the results by datasource id.
  const fetchEmbeddingStatusesForDatasources = async (sources: DataSource[], token: string) => {
    const channelIds = Array.from(
      new Set(sources.map((s) => s.channel?.id).filter((id): id is string => Boolean(id)))
    );

    if (channelIds.length === 0) return;

    try {
      const results = await Promise.all(
        channelIds.map(async (channelId) => {
          const response = await fetch(
            `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.EMBEDDING_STATUS}${channelId}`,
            {
              method: 'GET',
              headers: {
                accept: 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          );
          if (!response.ok) return null;
          return response.json();
        })
      );

      setEmbeddingStatusMap((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          if (!result?.datasources) return;
          result.datasources.forEach((ds: { id: string; embedding_status: number; error_message?: string | null }) => {
            next[ds.id] = { status: ds.embedding_status, error_message: ds.error_message };
          });
        });
        return next;
      });
    } catch (err) {
      console.error('Error fetching embedding status for admin datasources:', err);
    }
  };

  const fetchDatasources = async (page: number = 1, size: number = 10, search?: string) => {
    try {
      setDatasourcesLoading(true);
      setDatasourcesError(null);
      
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: DataSourcesResponse = await response.json();
      
      // API Response data
      // Datasources from API
      
      setDatasources(data.datasources);
      setDatasourcesTotal(data.total);
      setDatasourcesPage(data.page);
      void fetchEmbeddingStatusesForDatasources(data.datasources, token);

    } catch (err) {
             let errorMessage = `Failed to fetch ${LanguageConstants.datasource_s.toLowerCase()}`;
      
      if (err instanceof Error) {
        if (err.message.includes('HTTP error! status:')) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : '';
          
          switch (statusCode) {
            case '401':
              markSessionExpired();
              return;
            case '403':
              errorMessage = 'You do not have permission to access datasources.';
              break;
            case '404':
              errorMessage = 'Datasources not found.';
              break;
            case '500':
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Failed to load ${LanguageConstants.datasource_s.toLowerCase()} (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setDatasourcesError(errorMessage);
    } finally {
      setDatasourcesLoading(false);
    }
  };

  // Handle page change for pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setDatasourcesPage(value);
    fetchDatasources(value, pageSize, datasourcesSearch);
  };

  // Fetch users from API
  const fetchUsers = async (page: number = 1, size: number = 10, search?: string, role?: string, status?: string) => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      
      // Get company ID from localStorage (stored during login/registration)
      let companyId: string;
      try {
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          companyId = userData.company_id;
        } else {
          throw new Error('No user data found in localStorage');
        }
      } catch (error) {
        // Error retrieving company ID from localStorage
        setUsersError('Unable to retrieve company information. Please log in again.');
        return;
      }
      
      if (!companyId) {
        setUsersError('Company ID not found. Please contact support.');
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Add role filter if provided
      if (role && role.trim()) {
        params.append('role', role.trim());
      }
      
      // Add status filter if provided
      if (status && status.trim()) {
        params.append('status', status.trim());
      }
      
      const response = await userService.getCompanyUsers(companyId, page, size, search, role, status);
      
      // Users API Response
      
      setUsers(response.users);
      setUsersTotal(response.total);
      setUsersPage(response.page);
      
    } catch (err) {
      let errorMessage = 'Failed to fetch users';
      
      if (err instanceof Error) {
        if (err.message.includes('HTTP error! status:')) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : '';
          
          switch (statusCode) {
            case '401':
              markSessionExpired();
              return;
            case '403':
              errorMessage = 'You do not have permission to access users.';
              break;
            case '404':
              errorMessage = 'Users not found.';
              break;
            case '500':
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Failed to load users (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setUsersError(errorMessage);
    } finally {
      setUsersLoading(false);
    }
  };

  // Clamps a requested combined page to one category's own valid page range, so
  // navigating to a later combined page (driven by whichever category has the most
  // pages) never requests a page past a smaller category's real data — which would
  // otherwise silently come back empty and make that category look like it has no
  // results, even though its data still exists on its own earlier page (PR #620
  // review finding M1). The category's last valid page is re-fetched/shown instead.
  const clampToOwnPageRange = (page: number, total: number, size: number): number =>
    Math.min(page, Math.max(Math.ceil(total / size), 1));

  // Handle page change for users pagination
  // Coordinated pagination: one control advances Users, Pending Invitations, and
  // Access Requests together, since all three now render in a single combined table.
  // The displayed page number (usersPage/pendingUsersPage/accessRequestsPage, and the
  // Pagination control itself) stays the raw clicked value so it keeps matching
  // totalPages; only each category's own data FETCH is clamped to its valid range.
  const handleCombinedPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setUsersPage(value);
    setPendingUsersPage(value);
    setAccessRequestsPage(value);
    fetchUsers(clampToOwnPageRange(value, usersTotal, pageSize), pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
    fetchPendingUsers(clampToOwnPageRange(value, pendingUsersTotal, pageSize), pageSize, usersSearch, usersRoleFilter);
    fetchAccessRequests(clampToOwnPageRange(value, accessRequestsTotal, pageSize), pageSize, usersSearch, accessRequestsStatusFilter);
  };

  // Only show API data, no mock data fallback
  const displayUsers = users;

  // Handle search change — resets all three lists to page 1 since they share
  // one coordinated pagination control now (combined single-table view).
  const handleUsersSearchChange = (value: string) => {
    setUsersSearch(value);
    setUsersPage(1);
    setPendingUsersPage(1);
    setAccessRequestsPage(1);
  };

  // Handle role filter change (applies to Users + Pending Invitations)
  const handleRoleFilterChange = (value: string) => {
    setUsersRoleFilter(value);
    setUsersPage(1);
    setPendingUsersPage(1);
    setAccessRequestsPage(1);
  };

  // Handle status filter change (Users Active/Inactive)
  const handleStatusFilterChange = (value: string) => {
    setUsersStatusFilter(value);
    setUsersPage(1);
    setPendingUsersPage(1);
    setAccessRequestsPage(1);
  };

  // Handle request-status filter change (Access Requests Pending/Approved/Rejected)
  const handleAccessRequestsStatusFilterChange = (value: string) => {
    setAccessRequestsStatusFilter(value);
    setUsersPage(1);
    setPendingUsersPage(1);
    setAccessRequestsPage(1);
  };

  // "Onboarding" filter dropdown (replaces the old clickable summary cards): selecting a
  // value narrows the combined table to just that category; selecting "All" shows every
  // category together. Resets and refetches all three lists at page 1 so the coordinated
  // pagination stays consistent.
  const handleTypeFilterSelect = (value: 'all' | 'onboarded' | 'pending' | 'requests') => {
    setTypeFilter(value);
    setUsersPage(1);
    setPendingUsersPage(1);
    setAccessRequestsPage(1);
    fetchUsers(1, pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
    fetchPendingUsers(1, pageSize, usersSearch, usersRoleFilter);
    fetchAccessRequests(1, pageSize, usersSearch, accessRequestsStatusFilter);
  };

          // Fetch datasources when ${LanguageConstants.datasource_s} tab is active
  useEffect(() => {
    if (tabValue === 0) {
      fetchDatasources(1, pageSize, datasourcesSearch);
    }
  }, [tabValue]);

// Initial fetch when tab changes
  useEffect(() => {
    if (tabValue === 1) {
      // Triggered by tab activation — all three lists load together now that they
      // render combined in one table.
      fetchUsers(usersPage, pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
      fetchPendingUsers(pendingUsersPage, pageSize, usersSearch, usersRoleFilter);
      fetchAccessRequests(accessRequestsPage, pageSize, usersSearch, accessRequestsStatusFilter);
      refreshAccessRequestCount();
    }
  }, [tabValue]);

  const fetchPendingUsers = async (
    page = pendingUsersPage, 
    size = pageSize, 
    search = usersSearch, 
    role = usersRoleFilter
  ) => {
    try {
      setPendingUsersLoading(true);
      setPendingUsersError(null);
      const companyId = getCompanyId();
      if (!companyId) return;

      const response = await userService.getPendingInvitations(companyId, page, size, search, role);
      setPendingUsers(response.invitations || []);
      setPendingUsersTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
      setPendingUsersError('Failed to load pending users');
    } finally {
      setPendingUsersLoading(false);
    }
  };

  const refreshAccessRequestCount = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) return;
      const response = await userService.getAccessRequests(companyId, 1, 1, undefined, 'pending');
      setAccessRequestsPendingTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching access request count:', error);
    }
  };

  const fetchAccessRequests = async (
    page = accessRequestsPage,
    size = pageSize,
    search = usersSearch,
    status = accessRequestsStatusFilter
  ) => {
    try {
      setAccessRequestsLoading(true);
      setAccessRequestsError(null);
      const companyId = getCompanyId();
      if (!companyId) return;

      const response = await userService.getAccessRequests(companyId, page, size, search, status);
      setAccessRequests(response.access_requests || []);
      setAccessRequestsTotal(response.total || 0);
      if (!status || status === 'pending') {
        setAccessRequestsPendingTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching access requests:', error);
      setAccessRequestsError('Failed to load access requests');
    } finally {
      setAccessRequestsLoading(false);
    }
  };

  // Handle pending invitation deletion
  const handleDeletePendingInvitationClick = (invitation: PendingInvitation) => {
    setPendingInvitationToDelete(invitation);
    setDeletePendingInvitationModalOpen(true);
  };

  const handleDeletePendingInvitationModalClose = () => {
    setDeletePendingInvitationModalOpen(false);
    setPendingInvitationToDelete(null);
    setIsDeletingPendingInvitation(false);
  };

  const handleDeletePendingInvitationConfirm = async () => {
    if (!pendingInvitationToDelete) return;

    try {
      setIsDeletingPendingInvitation(true);
      
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      await userService.deletePendingInvitation(companyId, pendingInvitationToDelete.email);
      
      // Update the list locally
      setPendingUsers(prev => prev.filter(inv => inv.id !== pendingInvitationToDelete.id));
      setPendingUsersTotal(prev => prev - 1);
      
      // Close modal
      handleDeletePendingInvitationModalClose();
      toast.showSuccess('Invitation deleted successfully');
      
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast.showError('Failed to delete invitation');
    } finally {
      setIsDeletingPendingInvitation(false);
    }
  };

  const handleAccessRequestApprove = async (accessRequest: AccessRequest) => {
    setProcessingAccessRequestIds(prev => new Set(prev).add(accessRequest.id));
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const selectedRole = accessRequestRoleSelections[accessRequest.id] || 'user';
      await userService.approveAccessRequest(companyId, accessRequest.id, selectedRole);
      setAccessRequests(prev => prev.filter(item => item.id !== accessRequest.id));
      setAccessRequestsTotal(prev => Math.max(0, prev - 1));
      setAccessRequestRoleSelections(prev => {
        const { [accessRequest.id]: _removed, ...rest } = prev;
        return rest;
      });
      await Promise.all([
        refreshAccessRequestCount(),
        fetchPendingUsers(pendingUsersPage, pageSize, usersSearch, usersRoleFilter),
      ]);
      toast.showSuccess('Access request approved');
    } catch (error) {
      console.error('Error approving access request:', error);
      toast.showError(error instanceof Error ? error.message : 'Failed to approve access request');
      // The row may already be stale (e.g. resolved by another admin, or a double-click
      // racing itself) — re-fetch the current page so it reflects the true server state
      // instead of staying in the table looking clickable when it's already resolved.
      await fetchAccessRequests();
    } finally {
      setProcessingAccessRequestIds(prev => {
        const next = new Set(prev);
        next.delete(accessRequest.id);
        return next;
      });
    }
  };

  const handleAccessRequestReject = async (accessRequest: AccessRequest) => {
    setProcessingAccessRequestIds(prev => new Set(prev).add(accessRequest.id));
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      await userService.rejectAccessRequest(companyId, accessRequest.id);
      setAccessRequests(prev => prev.filter(item => item.id !== accessRequest.id));
      setAccessRequestsTotal(prev => Math.max(0, prev - 1));
      setAccessRequestRoleSelections(prev => {
        const { [accessRequest.id]: _removed, ...rest } = prev;
        return rest;
      });
      await refreshAccessRequestCount();
      toast.showSuccess('Access request rejected');
    } catch (error) {
      console.error('Error rejecting access request:', error);
      toast.showError(error instanceof Error ? error.message : 'Failed to reject access request');
      // Same re-sync as approve — don't leave a stale, still-clickable row on failure.
      await fetchAccessRequests();
    } finally {
      setProcessingAccessRequestIds(prev => {
        const next = new Set(prev);
        next.delete(accessRequest.id);
        return next;
      });
    }
  };

  const handleResendInvite = async (invitation: PendingInvitation) => {
    try {
      const companyId = getCompanyId();
      // Get user_id from localStorage as fallback
      const storedUserData = (() => {
        try {
          const raw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();
      const userId = user?.id || storedUserData?.user_id || '';
      
      await userService.resendInvitation({
        users: [{
          email: invitation.email,
          company_id: companyId,
          role: invitation.role
        }],
        user_id: userId,
        platform_name: "Prism 7"
      });
      
      toast.showSuccess('Invitation resent successfully');
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.showError('Failed to resend invitation');
    }
  };

  const handleDeleteInvite = async (invitation: PendingInvitation) => {
    if (!window.confirm(`Are you sure you want to delete the invitation for ${invitation.email}?`)) {
      return;
    }

    try {
      const companyId = getCompanyId();
      await userService.deleteUser(invitation.id, companyId);
      // Remove from list
      setPendingUsers(prev => prev.filter(p => p.id !== invitation.id));
      setPendingUsersTotal(prev => Math.max(0, prev - 1));
      toast.showSuccess('Invitation deleted successfully');
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast.showError('Failed to delete invitation');
    }
  };

  // Debounce search — fetches all three lists together (they render in one combined table)
  useEffect(() => {
    if (tabValue === 1) {
      const timer = setTimeout(() => {
        fetchUsers(1, pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
        fetchPendingUsers(1, pageSize, usersSearch, usersRoleFilter);
        fetchAccessRequests(1, pageSize, usersSearch, accessRequestsStatusFilter);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [usersSearch]);

  // Fetch all three lists when any filter changes
  useEffect(() => {
    if (tabValue === 1) {
      fetchUsers(1, pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
      fetchPendingUsers(1, pageSize, usersSearch, usersRoleFilter);
      fetchAccessRequests(1, pageSize, usersSearch, accessRequestsStatusFilter);
    }
  }, [usersRoleFilter, usersStatusFilter, accessRequestsStatusFilter]);

  // Fetch datasources when tab is active or search changes
  useEffect(() => {
    if (tabValue === 0) {
      fetchDatasources(1, pageSize, datasourcesSearch);
    }
  }, [tabValue, datasourcesSearch]);

  // Fetch workspaces when tab is active
  useEffect(() => {
    if (tabValue === 2) {
      fetchWorkspaces(workspacesPage, workspacesPageSize, workspacesSearch, workspacesSortBy, workspacesSortOrder);
    }
  }, [tabValue]);

  // Fetch subscription when tab is active (only when subscription feature is enabled)
  useEffect(() => {
    if (!DISABLE_SUBSCRIPTION && tabValue === 3) {
      fetchSubscriptionData();
    }
  }, [tabValue]);

  // Debounce search for workspaces
  useEffect(() => {
    if (tabValue === 2) {
      const timer = setTimeout(() => {
        fetchWorkspaces(1, workspacesPageSize, workspacesSearch, workspacesSortBy, workspacesSortOrder);
        setWorkspacesPage(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [workspacesSearch]);

  // Page changes are handled directly by handleCombinedPageChange (advances all
  // three lists in lockstep), so no separate per-list page-change effect is needed.

  // Fetch subscription data and invoice details when Subscription Management tab is active (only when subscription enabled)
  useEffect(() => {
    if (!DISABLE_SUBSCRIPTION && tabValue === 3) {
      fetchSubscriptionData();
      fetchAvailablePlans();
      fetchInvoiceDetails();
    }
  }, [tabValue]);

  // Handle search change
  const handleSearchChange = (value: string) => {
    setDatasourcesSearch(value);
    // Reset pagination when searching
    if (value !== datasourcesSearch) {
      setDatasourcesPage(1);
      fetchDatasources(1, pageSize, value);
      // Clear any bulk-select made under the previous search — otherwise a stale
      // selection from before the search changed can drive an irreversible bulk
      // delete on items no longer even visible (PR #621 review finding M1).
      setSelectedDatasourceIds(new Set());
    }
  };


  // Helper function to get shortened display name for file types
  const getShortTypeName = (type: string | undefined): string => {
    // getShortTypeName called with type
    
    if (!type) return 'Unknown';
    
    const typeLower = type.toLowerCase();
    // typeLower
    
    // Handle MIME types
    if (typeLower.includes('/')) {
      const parts = typeLower.split('/');
      if (parts.length === 2) {
        const subtype = parts[1];
        // MIME type detected, subtype
        switch (subtype) {
          case 'plain':
            return 'txt';
          case 'html':
            return 'html';
          case 'css':
            return 'css';
          case 'javascript':
          case 'js':
            return 'js';
          case 'json':
            return 'json';
          case 'xml':
            return 'xml';
          case 'csv':
            return 'csv';
          case 'pdf':
            return 'pdf';
          case 'png':
            return 'png';
          case 'jpeg':
          case 'jpg':
            return 'jpg';
          case 'gif':
            return 'gif';
          case 'svg':
            return 'svg';
          case 'zip':
            return 'zip';
          case 'rar':
            return 'rar';
          case 'doc':
            return 'doc';
          case 'docx':
            return 'docx';
          case 'xls':
            return 'xls';
          case 'xlsx':
            return 'xlsx';
          case 'ppt':
            return 'ppt';
          case 'pptx':
            return 'pptx';
          default:
            return subtype;
        }
      }
    }
    
    // Handle file extensions
    if (typeLower.startsWith('.')) {
      return typeLower.substring(1);
    }
    
    // Handle common file types
    switch (typeLower) {
      case 'url':
      case 'web':
        return 'url';
      case 'shay-ai':
      case 'ai':
        return 'ai';
      case 'pdf':
        return 'pdf';
      case 'html':
        return 'html';
      case 'text':
        return 'txt';
      case 'document':
        return 'doc';
      case 'spreadsheet':
        return 'xls';
      case 'presentation':
        return 'ppt';
      case 'image':
        return 'img';
      case 'video':
        return 'vid';
      case 'audio':
        return 'aud';
      default:
        // No match found, returning original type
        return typeLower;
    }
  };

  // Status tag for a datasource's embedding state, sourced only from
  // GET /datasources/embedding-status/{channel_id} (embeddingStatusMap) — the same API the
  // Data Sources panel's Embedding column uses. Renders an empty chip (no guessed label) until
  // that real response arrives for this row's channel.
  const getProcessingStatusChip = (source: DataSource) => {
    const embedding = embeddingStatusMap[source.id];

    if (!embedding) {
      return <Chip label="" size="small" color="default" sx={{ fontWeight: 500, height: '20px' }} />;
    }

    let label: string;
    let color: 'success' | 'error' | 'warning' | 'default';

    switch (embedding.status) {
      case 1:
        label = 'In Progress';
        color = 'warning';
        break;
      case 2:
        label = 'Completed';
        color = 'success';
        break;
      case 3:
        label = 'Failed';
        color = 'error';
        break;
      default:
        label = 'Unknown';
        color = 'default';
    }

    const chip = <Chip label={label} size="small" color={color} sx={{ fontWeight: 500 }} />;
    return embedding.status === 3 && embedding.error_message ? (
      <Tooltip title={embedding.error_message}>{chip}</Tooltip>
    ) : chip;
  };

  // Default subscription data (fallback)
  const defaultSubscriptionData: SubscriptionData = {
    planName: 'Professional Plan',
    billingCycle: 'Monthly',
    cost: '$99.00',
    nextRenewal: '2024-02-15',
    lastPayment: '2024-01-15',
    usage: {
      users: { current: 5, limit: 10 },
      storage: { current: '2.5 GB', limit: '10 GB' },
      apiCalls: { current: 15000, limit: 50000 },
    },
  };

  // Use real data or fallback to default
  const currentSubscriptionData = subscriptionData || defaultSubscriptionData;

          // Mock data for ${LanguageConstants.datasource_s.toLowerCase()}
  const dataSources = [
    {
      id: 1,
      name: 'Sales Database',
      type: 'Database',
      connection: 'MySQL',
      status: 'Active',
      lastSync: '2024-01-20 10:00',
    },
    {
      id: 2,
      name: 'API Integration',
      type: 'API',
      connection: 'REST',
      status: 'Inactive',
      lastSync: '2024-01-18 14:30',
    },
    {
      id: 3,
      name: 'File Storage',
      type: 'File',
      connection: 'S3',
      status: 'Error',
      lastSync: '2024-01-15 09:00',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'error';
      default:
        return 'default';
    }
  };



  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str: string): string => {
    if (!str) return '';
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <Box sx={{ 
      backgroundColor: 'transparent'
    }}>
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        mb: 3,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        px: 2
      }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="admin tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              minHeight: 48,
              color: 'text.primary',
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3,
            }
          }}
        >
          <Tab 
            label={ADMIN_HUB_TABS.DATA_SOURCES.label} 
            icon={<Storage />} 
            iconPosition="start"
          />
          <Tab 
            label={ADMIN_HUB_TABS.USER_MANAGEMENT.label} 
            icon={<People />} 
            iconPosition="start"
          />
          <Tab 
            label={ADMIN_HUB_TABS.WORKSPACE_MANAGEMENT.label} 
            icon={<Workspaces />} 
            iconPosition="start"
          />
          {!DISABLE_SUBSCRIPTION && (
            <Tab 
              label={ADMIN_HUB_TABS.SUBSCRIPTION_MANAGEMENT.label} 
              icon={<CreditCard />} 
              iconPosition="start"
            />
          )}
          {/* Chat Service Management only visible when subscription is disabled */}
          {DISABLE_SUBSCRIPTION ? (
            <Tab 
              label={ADMIN_HUB_TABS.CHAT_SERVICE_MANAGEMENT.label} 
              icon={<ChatIcon />} 
              iconPosition="start"
            />
          ) : null}
        </Tabs>
      </Box>

      {/* User Management Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>

          {/* Search, Filters and Add User Button - All in one row.
              The old clickable summary cards (Onboarded Users / Pending Invitations /
              Access Requests) are replaced by the "Onboarding" dropdown right after the
              search box — same three categories plus "All", same handleTypeFilterSelect
              wiring, just a filter control instead of a row of cards. */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder="Search users, invitations, or requests..."
              value={usersSearch}
              onChange={(e) => handleUsersSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 250,
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
              }}
              size="small"
            />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Onboarding</InputLabel>
              <Select
                label="Onboarding"
                value={typeFilter}
                onChange={(e) => handleTypeFilterSelect(e.target.value as 'all' | 'onboarded' | 'pending' | 'requests')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="onboarded">Onboarded Users ({usersTotal})</MenuItem>
                <MenuItem value="pending">Pending Invitations ({pendingUsersTotal})</MenuItem>
                <MenuItem value="requests">Access Requests ({accessRequestsPendingTotal})</MenuItem>
              </Select>
            </FormControl>
            {/* All filters now always visible together (Usermanagement_filter_upgrade):
                cards no longer gate which controls show, since all 3 categories render
                combined in one table. "Request Status" is labeled separately from the
                Users "Status" filter since both are now on screen at once. */}
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={usersRoleFilter}
                onChange={(e) => handleRoleFilterChange(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={usersStatusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Request Status</InputLabel>
              <Select
                label="Request Status"
                value={accessRequestsStatusFilter}
                onChange={(e) => handleAccessRequestsStatusFilterChange(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<Add />} onClick={handleAddUserModalOpen}>
              Add User
            </Button>
          </Box>

          {/* Combined Users + Pending Invitations + Access Requests table (Usermanagement_filter_upgrade):
              all three categories render together, always — the summary cards above are now static
              counters, not filters. A "Type" column identifies each row, and one Pagination control
              advances all three lists in lockstep (handleCombinedPageChange) instead of three
              independent paginations. Each row's fields/handlers are otherwise unchanged from before. */}
          {(() => {
            const isLoading = usersLoading || pendingUsersLoading || accessRequestsLoading;
            const errorMsg = usersError || pendingUsersError || accessRequestsError;
            // Card click-to-filter: which categories are visible right now.
            const showUsers = typeFilter === 'all' || typeFilter === 'onboarded';
            const showInvitations = typeFilter === 'all' || typeFilter === 'pending';
            const showRequests = typeFilter === 'all' || typeFilter === 'requests';
            const visibleRowCount =
              (showUsers ? displayUsers.length : 0) +
              (showInvitations ? pendingUsers.length : 0) +
              (showRequests ? accessRequests.length : 0);
            const noResultsAtAll = visibleRowCount === 0;
            const filterNoun = typeFilter === 'all'
              ? 'users, invitations, or requests'
              : typeFilter === 'onboarded' ? 'users' : typeFilter === 'pending' ? 'invitations' : 'requests';
            const emptyText = (usersSearch || usersRoleFilter || usersStatusFilter)
              ? `No ${filterNoun} found matching your filters`
              : `No ${filterNoun} found`;
            // usersPage/pendingUsersPage/accessRequestsPage are kept equal by
            // handleCombinedPageChange — usersPage is used as the single displayed page number.
            const combinedPage = usersPage;
            const totalPages = Math.max(
              showUsers ? Math.ceil(usersTotal / pageSize) : 0,
              showInvitations ? Math.ceil(pendingUsersTotal / pageSize) : 0,
              showRequests ? Math.ceil(accessRequestsTotal / pageSize) : 0,
              1
            );
            const totalRows =
              (showUsers ? usersTotal : 0) +
              (showInvitations ? pendingUsersTotal : 0) +
              (showRequests ? accessRequestsTotal : 0);
            const handleRetry = () => {
              fetchUsers(combinedPage, pageSize, usersSearch, usersRoleFilter, usersStatusFilter);
              fetchPendingUsers(combinedPage, pageSize, usersSearch, usersRoleFilter);
              fetchAccessRequests(combinedPage, pageSize, usersSearch, accessRequestsStatusFilter);
            };

            return (
              <>
                <TableContainer component={Paper} sx={{
                  ...getTableContainerStyles(),
                  backgroundColor: 'background.paper',
                  boxShadow: 2,
                }}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                        Loading users, invitations, and requests...
                      </Typography>
                    </Box>
                  ) : errorMsg ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
                      <Button variant="outlined" onClick={handleRetry}>Retry</Button>
                    </Box>
                  ) : (
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Type</TableCell>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Email</TableCell>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Role</TableCell>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Status</TableCell>
                          {/* Approved By / Approved At (from access_request_tabular, merged from
                              APP_PRISM7_DEV) — Access-Request-only fields, so they read '-' on
                              User/Invitation rows rather than being dropped from the shared header. */}
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Approved By</TableCell>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Approved At</TableCell>
                          <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {noResultsAtAll ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                {emptyText}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {(showUsers ? displayUsers : []).map((rowUser: User) => (
                              <TableRow key={`user-${rowUser.id}`} hover sx={getTableRowStyles()}>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip label="User" size="small" variant="outlined" />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Typography variant="body2">{rowUser.email_id}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {capitalizeWords(rowUser.name)}
                                    {rowUser.last_login ? ` · Last login ${formatDisplayDateTime(rowUser.last_login)}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>{capitalizeWords(rowUser.role)}</TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip
                                    label={rowUser.is_active ? 'Active' : 'Inactive'}
                                    color={getStatusColor(rowUser.is_active ? 'Active' : 'Inactive') as any}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>-</TableCell>
                                <TableCell sx={getTableCellStyles()}>-</TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  {/* Hide edit/delete for the currently logged-in user (match by id or email) */}
                                  {(() => {
                                    const isCurrentUser =
                                      (user?.id && String(rowUser.id) === String(user.id)) ||
                                      (user?.email && rowUser.email_id?.toLowerCase() === user.email.toLowerCase());
                                    return !isCurrentUser ? (
                                      <>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleEditUserClick(rowUser)}
                                        >
                                          <Edit />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteUserClick(rowUser)}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </>
                                    ) : null;
                                  })()}
                                </TableCell>
                              </TableRow>
                            ))}
                            {(showInvitations ? pendingUsers : []).map((invitation: PendingInvitation) => (
                              <TableRow key={`invitation-${invitation.id}`} hover sx={getTableRowStyles()}>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip label="Invitation" size="small" variant="outlined" color="secondary" />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Typography variant="body2">{invitation.email}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {invitation.invited_by?.username ? `Invited by ${invitation.invited_by.username}` : 'Invited'}
                                    {invitation.expires_at ? ` · Expires ${formatDisplayDateTime(invitation.expires_at)}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>{capitalizeWords(invitation.role)}</TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip label="Invited" color="secondary" size="small" />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>-</TableCell>
                                <TableCell sx={getTableCellStyles()}>-</TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <IconButton size="small" color="primary" onClick={() => handleResendInvite(invitation)} title="Resend Invite">
                                    <Send />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleDeletePendingInvitationClick(invitation)} title="Delete Invite">
                                    <Delete />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {(showRequests ? accessRequests : []).map((accessRequest: AccessRequest) => (
                              <TableRow key={`request-${accessRequest.id}`} hover sx={getTableRowStyles()}>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip label="Request" size="small" variant="outlined" color="warning" />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Typography variant="body2">{accessRequest.email}</Typography>
                                  {accessRequest.requested_at && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      Requested {formatDisplayDateTime(accessRequest.requested_at)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  {accessRequest.status === 'pending' ? (
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                      <Select
                                        value={accessRequestRoleSelections[accessRequest.id] || 'user'}
                                        onChange={(e) =>
                                          setAccessRequestRoleSelections(prev => ({
                                            ...prev,
                                            [accessRequest.id]: e.target.value as 'admin' | 'user',
                                          }))
                                        }
                                        disabled={processingAccessRequestIds.has(accessRequest.id)}
                                      >
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="user">User</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  <Chip
                                    label={capitalizeWords(accessRequest.status)}
                                    color={
                                      accessRequest.status === 'approved'
                                        ? 'success'
                                        : accessRequest.status === 'rejected'
                                          ? 'error'
                                          : 'warning'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  {accessRequest.reviewed_by?.userEmail || accessRequest.reviewed_by?.username || '-'}
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  {accessRequest.reviewed_at ? formatDisplayDateTime(accessRequest.reviewed_at) : '-'}
                                </TableCell>
                                <TableCell sx={getTableCellStyles()}>
                                  {accessRequest.status === 'pending' ? (
                                    <>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleAccessRequestApprove(accessRequest)}
                                        disabled={processingAccessRequestIds.has(accessRequest.id)}
                                        title="Accept Access Request"
                                      >
                                        <CheckCircle />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleAccessRequestReject(accessRequest)}
                                        disabled={processingAccessRequestIds.has(accessRequest.id)}
                                        title="Reject Access Request"
                                      >
                                        <Cancel />
                                      </IconButton>
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>

                {!isLoading && !errorMsg && totalRows > 0 && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    px: 2,
                    py: 2,
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: 'background.paper'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      {typeFilter === 'all'
                        ? `Page ${combinedPage} of ${totalPages} · ${usersTotal} users, ${pendingUsersTotal} invitations, ${accessRequestsTotal} requests`
                        : `Page ${combinedPage} of ${totalPages} · ${totalRows} ${filterNoun}`
                      }
                    </Typography>

                    {totalPages > 1 && (
                      <Pagination
                        count={totalPages}
                        page={combinedPage}
                        onChange={handleCombinedPageChange}
                        color="primary"
                        size="small"
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                              color: 'white'
                            }
                          }
                        }}
                      />
                    )}
                  </Box>
                )}
              </>
            );
          })()}
        </Box>
      </TabPanel>

      {/* Subscription Management Tab - only when subscription feature is enabled */}
      {!DISABLE_SUBSCRIPTION && (
      <TabPanel value={tabValue} index={3}>
        <Box>
          {subscriptionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : subscriptionError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {subscriptionError}
            </Alert>
          ) : (
            <>
          {/* Current Plan and Usage Statistics in same row */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            {/* Current Plan Information - Left Side */}
            <Card sx={{ flex: 1, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Plan
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Plan Name
                    </Typography>
                        <Typography variant="body2">{currentSubscriptionData.planName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Billing Cycle
                    </Typography>
                        <Typography variant="body2">{currentSubscriptionData.billingCycle}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Cost
                    </Typography>
                        <Typography variant="body2">{currentSubscriptionData.cost}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Next Renewal
                    </Typography>
                        <Typography variant="body2">
                          {currentSubscriptionData.nextRenewal ? formatDisplayDate(currentSubscriptionData.nextRenewal) : '—'}
                        </Typography>
                  </Box>
                </Box>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                      onClick={() => setPlansModalOpen(true)}
                >
                  Manage Plan
                </Button>
              </CardContent>
            </Card>

            {/* Usage Statistics - Right Side (from fetchCompanySubscriptionDetails API) */}
            <Card sx={{ flex: 1, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Usage Statistics
                </Typography>
                {invoicesLoading && !usageStats ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">Loading usage...</Typography>
                  </Box>
                ) : usageStats ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Channels: used/limit, percentage */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ width: 240, flexShrink: 0 }}>
                        Channels ({usageStats.channels.used}/{usageStats.channels.limit})
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usageStats.channels.limit > 0 ? Math.min(100, Math.round((usageStats.channels.used / usageStats.channels.limit) * 100)) : 0}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 40, flexShrink: 0, textAlign: 'right' }}>
                        {usageStats.channels.limit > 0 ? `${Math.min(100, Math.round((usageStats.channels.used / usageStats.channels.limit) * 100))}%` : '0%'}
                      </Typography>
                    </Box>
                    {/* Messages: used/limit, percentage */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ width: 240, flexShrink: 0 }}>
                        Messages ({usageStats.messages.used}/{usageStats.messages.limit})
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usageStats.messages.limit > 0 ? Math.min(100, Math.round((usageStats.messages.used / usageStats.messages.limit) * 100)) : 0}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 40, flexShrink: 0, textAlign: 'right' }}>
                        {usageStats.messages.limit > 0 ? `${Math.min(100, Math.round((usageStats.messages.used / usageStats.messages.limit) * 100))}%` : '0%'}
                      </Typography>
                    </Box>
                    {/* Data Storage: adaptive MB/GB from bytes (same helper as upgrade modal) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ width: 240, flexShrink: 0 }}>
                        Data Storage ({formatSubscriptionStorageUsedOfLimit(usageStats.storage.usedBytes, usageStats.storage.limitBytes)})
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usageStats.storage.limitBytes > 0 ? Math.min(100, Math.round((usageStats.storage.usedBytes / usageStats.storage.limitBytes) * 100)) : 0}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ width: 40, flexShrink: 0, textAlign: 'right' }}>
                        {usageStats.storage.limitBytes > 0 ? `${Math.min(100, Math.round((usageStats.storage.usedBytes / usageStats.storage.limitBytes) * 100))}%` : '0%'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No usage data available</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
            </>
          )}

          {/* Recent Invoices - populated from fetchCompanySubscriptionDetails API */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Invoices
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search invoices..."
                    value={invoicesSearch}
                    onChange={(e) => setInvoicesSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      minWidth: 250,
                      backgroundColor: 'background.paper',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                      },
                    }}
                    size="small"
                  />
                  {/* <Button startIcon={<Download />} variant="outlined">
                    Download
                  </Button> */}
                </Box>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{
                ...getTableContainerStyles(),
                backgroundColor: 'background.paper',
                boxShadow: 2,
              }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Invoice No.</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Plan Name</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Recent Payment</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Invoice Total</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Status</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicesLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary" component="span">
                            Loading invoices...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : invoicesError ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="body2" color="error">{invoicesError}</Typography>
                          <Button size="small" onClick={fetchInvoiceDetails} sx={{ mt: 1 }}>
                            Retry
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                      const searchLower = invoicesSearch.trim().toLowerCase();
                      const filtered = searchLower
                        ? invoiceDetails.filter(
                            (inv) =>
                              inv.number.toLowerCase().includes(searchLower) ||
                              inv.status.toLowerCase().includes(searchLower)
                          )
                        : invoiceDetails;
                      if (filtered.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                No invoices found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filtered.map((inv) => (
                        <TableRow key={inv.invoice_id} sx={getTableRowStyles()}>
                          <TableCell sx={getTableCellStyles()}>
                            <Typography
                              component="button"
                              onClick={() => {
                                setSelectedInvoice(inv);
                                setViewInvoiceModalOpen(true);
                              }}
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                textAlign: 'left',
                              }}
                            >
                              {inv.number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>{inv.subscriptionPlan?.name ?? '—'}</TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            {inv.payment_made ?? inv.invoice_date ?? '—'}
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            {inv.currency_symbol}{inv.total}
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <Chip label={capitalizeFirstLetter(inv.status)} size="small" color={inv.status === 'paid' ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                setInvoiceDownloadingId(inv.invoice_id);
                                try {
                                  await downloadInvoice(inv.invoice_id, PLATFORM_NAME, `${inv.number}.pdf`);
                                } catch (err) {
                                  console.error('Invoice download failed:', err);
                                } finally {
                                  setInvoiceDownloadingId(null);
                                }
                              }}
                              disabled={invoiceDownloadingId === inv.invoice_id}
                              aria-label={`Download invoice ${inv.number}`}
                            >
                              {invoiceDownloadingId === inv.invoice_id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Download />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      )}

              {/* ${LanguageConstants.datasource_s} Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3 }}>
          {/* Search and Add Log ${LanguageConstants.datasource} Button - All in one row */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 3, 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder={`Search ${LanguageConstants.datasource_s.toLowerCase()}...`}
              value={datasourcesSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: 250,
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
              }}
              size="small"
            />
            <Box sx={{ flexGrow: 1 }} />
            {selectedDatasourceIds.size > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleBulkDeleteClick}
              >
                Delete Selected ({selectedDatasourceIds.size})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleModalOpen}
            >
              Add {LanguageConstants.datasource}
            </Button>
          </Box>

          {/* Log ${LanguageConstants.datasource_s} Table */}
          <TableContainer 
            component={Paper} 
            sx={{
              ...getTableContainerStyles(),
              backgroundColor: 'background.paper',
              boxShadow: 2,
              width: '100%',
              '& .MuiTable-root': {
                width: '100%',
              },
            }}
          >
            {/* Loading state */}
            {datasourcesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading {LanguageConstants.datasource_s.toLowerCase()}...
                </Typography>
              </Box>
            )}

            {/* Error state */}
            {datasourcesError && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {datasourcesError}
                </Alert>
                <Button 
                  variant="outlined" 
                  onClick={() => fetchDatasources(1, pageSize)}
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </Box>
            )}

            {/* Data table */}
            {!datasourcesLoading && !datasourcesError && (
              <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '4%' }}>
                      <Checkbox
                        size="small"
                        checked={datasourcesTotal > 0 && selectedDatasourceIds.size === datasourcesTotal}
                        indeterminate={selectedDatasourceIds.size > 0 && selectedDatasourceIds.size < datasourcesTotal}
                        onChange={handleToggleSelectAllDatasources}
                        disabled={isSelectingAllDatasources}
                        inputProps={{ 'aria-label': 'Select all data sources across all pages' }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '18%' }}>Source Name</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '9%' }}>Type</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '15%' }}>Added By</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '11%' }}>Added On</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '11%' }}>Status</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '22%' }}>Support {LanguageConstants.topic}</TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), width: '10%' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datasources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                                                     {datasourcesSearch ? `No ${LanguageConstants.datasource_s.toLowerCase()} found matching your search` : `No ${LanguageConstants.datasource_s.toLowerCase()} found`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    datasources.map((source) => {
                      // Rendering source
                      return (
                        <TableRow key={source.id} hover sx={getTableRowStyles()}>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Checkbox
                              size="small"
                              checked={selectedDatasourceIds.has(source.id)}
                              onChange={() => toggleDatasourceSelected(source.id)}
                              inputProps={{ 'aria-label': `Select ${source.name || source.filename || 'data source'}` }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              {getDataSourceIcon(source)}
                              <Typography 
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                  flex: 1
                                }}
                              >
                                {source.name || source.filename || 'Unnamed Datasource'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Typography 
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {getShortTypeName(source.file_type)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <People sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                              <Typography 
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                  flex: 1
                                }}
                              >
                                {capitalizeWords(source.added_by_user?.name || 'Unknown')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Typography 
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {source.created_at ? formatDisplayDate(source.created_at) : 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            {getProcessingStatusChip(source)}
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <Typography
                              onClick={() => {
                                const channelId = source.channel?.id || source.datasource_metadata?.channelId;
                                if (channelId) {
                                  navigate(`/threads/${channelId}`);
                                }
                              }} 
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {capitalizeFirstLetter(source.channel?.name) || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...getTableCellStyles(), overflow: 'hidden' }}>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteClick(source)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          
          {/* Pagination - Outside the table container */}
          {!datasourcesLoading && !datasourcesError && datasourcesTotal > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 2, 
              px: 2,
              py: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'background.paper'
            }}>
              {/* Pagination Info */}
              <Typography variant="body2" color="text.secondary">
                                 {datasourcesTotal > pageSize 
                   ? `Showing ${((datasourcesPage - 1) * pageSize) + 1} to ${Math.min(datasourcesPage * pageSize, datasourcesTotal)} of ${datasourcesTotal} ${LanguageConstants.datasource_s.toLowerCase()}`
                   : `Showing ${datasourcesTotal} of ${datasourcesTotal} ${LanguageConstants.datasource_s.toLowerCase()}`
                 }
              </Typography>
              
              {/* Pagination Controls - only show if multiple pages */}
              {datasourcesTotal > pageSize && (
                <Pagination
                  count={Math.ceil(datasourcesTotal / pageSize)}
                  page={datasourcesPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        color: 'white'
                      }
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </TabPanel>

              {/* Add Log ${LanguageConstants.datasource} Modal */}
      <AddLogDataSourceModal
        open={openModal}
        onClose={handleModalClose}
        onDataSourceAdded={handleDataSourceAdded}
      />

             {/* Delete Confirmation Modal */}
       <Dialog
         open={deleteModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') {
             return; // Prevent closing when clicking outside
           }
           handleDeleteModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: 3,
           }
         }}
       >
                  <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Delete sx={{ color: 'error.main', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Delete {LanguageConstants.datasource}
              </Typography>
            </Box>
            <IconButton
              onClick={handleDeleteModalClose}
              disabled={isDeleting}
              sx={{ color: 'error.main' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
         
         <DialogContent sx={{ pt: 1 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
              Upon deletion, the {LanguageConstants.datasource.toLowerCase()} (
                           <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {datasourceToDelete && getDataSourceIcon(datasourceToDelete)}
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'error.main',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block'
                  }}
                >
                  {datasourceToDelete?.name || datasourceToDelete?.filename || 'Unknown'}
                </Box>
              </Box>
             ) will be unavailable for additional queries within the <Box component="span" sx={{ fontWeight: 'bold' }}>{datasourceToDelete?.channel?.name || 'Unknown'}</Box> space and throughout all previous chat sessions to which it was attached.
            </Typography>
         </DialogContent>
         
         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button
             onClick={handleDeleteModalClose}
             disabled={isDeleting}
             sx={{ 
               color: 'text.secondary',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
             }}
           >
             Cancel
           </Button>
           <Button
             onClick={handleDeleteConfirm}
             disabled={isDeleting}
             variant="contained"
             color="error"
             sx={{ 
               backgroundColor: 'error.main',
               '&:hover': { backgroundColor: 'error.dark' },
               '&:disabled': { backgroundColor: 'error.light' }
             }}
             startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isDeleting ? 'Deleting...' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Bulk Delete Data Sources Modal — reuses the same single-item DELETE call per
           selected id (handleBulkDeleteConfirm), since no bulk-delete API exists. */}
       <Dialog
         open={bulkDeleteModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') {
             return; // Prevent closing when clicking outside
           }
           handleBulkDeleteModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: 3,
           }
         }}
       >
         <DialogTitle sx={{
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'space-between',
           pb: 1
         }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Delete sx={{ color: 'error.main', fontSize: 24 }} />
             <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
               Delete {selectedDatasourceIds.size} {LanguageConstants.datasource_s}
             </Typography>
           </Box>
           <IconButton
             onClick={handleBulkDeleteModalClose}
             disabled={isBulkDeleting}
             sx={{ color: 'error.main' }}
           >
             <CloseIcon />
           </IconButton>
         </DialogTitle>

         <DialogContent sx={{ pt: 1 }}>
           <Typography variant="body1" sx={{ mb: 2 }}>
             Upon deletion, the selected <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>{selectedDatasourceIds.size}</Box> {LanguageConstants.datasource_s.toLowerCase()} will be unavailable for additional queries within their spaces and throughout all previous chat sessions they were attached to. This cannot be undone.
           </Typography>
         </DialogContent>

         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button
             onClick={handleBulkDeleteModalClose}
             disabled={isBulkDeleting}
             sx={{
               color: 'text.secondary',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
             }}
           >
             Cancel
           </Button>
           <Button
             onClick={handleBulkDeleteConfirm}
             disabled={isBulkDeleting}
             variant="contained"
             color="error"
             sx={{
               backgroundColor: 'error.main',
               '&:hover': { backgroundColor: 'error.dark' },
               '&:disabled': { backgroundColor: 'error.light' }
             }}
             startIcon={isBulkDeleting ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isBulkDeleting ? 'Deleting...' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Edit User Modal */}
       <Dialog
         open={editUserModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') {
             return; // Prevent closing when clicking outside
           }
           handleEditUserModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: 3,
           }
         }}
       >
         <DialogTitle sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           justifyContent: 'space-between',
           pb: 1
         }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Edit sx={{ color: 'primary.main', fontSize: 24 }} />
             <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
               Edit User
             </Typography>
           </Box>
           <IconButton
             onClick={handleEditUserModalClose}
             disabled={isUpdating}
             sx={{ color: 'text.secondary' }}
           >
             <CloseIcon />
           </IconButton>
         </DialogTitle>
         
         <DialogContent sx={{ pt: 1 }}>
           {userToEdit && (
             <Box sx={{ pt: 1 }}>
               {/* Name - Non-editable */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Name
                 </Typography>
                 <TextField
                   value={userToEdit.name}
                   fullWidth
                   disabled
                   sx={{ 
                     '& .MuiInputBase-input.Mui-disabled': {
                       WebkitTextFillColor: 'text.primary',
                       color: 'text.primary',
                     }
                   }}
                 />
               </Box>

               {/* Email - Non-editable */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Email
                 </Typography>
                 <TextField
                   value={userToEdit.email_id}
                   fullWidth
                   disabled
                   sx={{ 
                     '& .MuiInputBase-input.Mui-disabled': {
                       WebkitTextFillColor: 'text.primary',
                       color: 'text.primary',
                     }
                   }}
                 />
               </Box>

               {/* Status - Editable */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Status
                 </Typography>
                 <FormControl fullWidth>
                   <Select
                     value={editFormData.is_active.toString()}
                     onChange={(e) => handleEditFormChange('is_active', e.target.value === 'true')}
                     size="small"
                   >
                     <MenuItem value="true">Active</MenuItem>
                     <MenuItem value="false">Inactive</MenuItem>
                   </Select>
                 </FormControl>
               </Box>

               {/* Role - Editable */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Role
                 </Typography>
                 <FormControl fullWidth>
                   <Select
                     value={editFormData.role}
                     onChange={(e) => handleEditFormChange('role', e.target.value)}
                     size="small"
                   >
                     <MenuItem value="admin">Admin</MenuItem>
                     <MenuItem value="user">User</MenuItem>
                   </Select>
                 </FormControl>
               </Box>
             </Box>
           )}
         </DialogContent>
         
         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button
             onClick={handleEditUserModalClose}
             disabled={isUpdating}
             sx={{ 
               color: 'text.secondary',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
             }}
           >
             Cancel
           </Button>
           <Button
             onClick={handleUpdateUser}
             disabled={isUpdating}
             variant="contained"
             color="primary"
             sx={{ 
               backgroundColor: 'primary.main',
               '&:hover': { backgroundColor: 'primary.dark' },
               '&:disabled': { backgroundColor: 'primary.light' }
             }}
             startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isUpdating ? 'Updating...' : 'Update'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Workspace Management Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box>


          {/* Search and Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search workspaces..."
              value={workspacesSearch}
              onChange={(e) => setWorkspacesSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ width: 300 }}
            />
          </Box>

          {/* Workspaces Table */}
          <TableContainer component={Paper} sx={{
            ...getTableContainerStyles(),
            backgroundColor: 'background.paper',
            boxShadow: 2,
          }}>
            {workspacesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading workspaces...
                </Typography>
              </Box>
            ) : workspacesError ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{workspacesError}</Alert>
                <Button variant="outlined" onClick={() => fetchWorkspaces(workspacesPage, workspacesPageSize, workspacesSearch, workspacesSortBy, workspacesSortOrder)}>Retry</Button>
              </Box>
            ) : (
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), cursor: 'pointer' }}
                      onClick={() => {
                        const newOrder = workspacesSortBy === 'name' && workspacesSortOrder === 'asc' ? 'desc' : 'asc';
                        setWorkspacesSortBy('name');
                        setWorkspacesSortOrder(newOrder);
                        fetchWorkspaces(workspacesPage, workspacesPageSize, workspacesSearch, 'name', newOrder);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Workspace Name
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <ArrowUpward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'name' && workspacesSortOrder === 'asc' ? 1 : 0.3,
                              color: workspacesSortBy === 'name' && workspacesSortOrder === 'asc' ? 'primary.main' : 'inherit'
                            }} 
                          />
                          <ArrowDownward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'name' && workspacesSortOrder === 'desc' ? 1 : 0.3,
                              color: workspacesSortBy === 'name' && workspacesSortOrder === 'desc' ? 'primary.main' : 'inherit',
                              marginLeft: '-2px'
                            }} 
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Description</TableCell>
                    <TableCell 
                      sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), cursor: 'pointer' }}
                      onClick={() => {
                        const newOrder = workspacesSortBy === 'channel_count' && workspacesSortOrder === 'asc' ? 'desc' : 'asc';
                        setWorkspacesSortBy('channel_count');
                        setWorkspacesSortOrder(newOrder);
                        fetchWorkspaces(workspacesPage, workspacesPageSize, workspacesSearch, 'channel_count', newOrder);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        No of Channels
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <ArrowUpward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'channel_count' && workspacesSortOrder === 'asc' ? 1 : 0.3,
                              color: workspacesSortBy === 'channel_count' && workspacesSortOrder === 'asc' ? 'primary.main' : 'inherit'
                            }} 
                          />
                          <ArrowDownward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'channel_count' && workspacesSortOrder === 'desc' ? 1 : 0.3,
                              color: workspacesSortBy === 'channel_count' && workspacesSortOrder === 'desc' ? 'primary.main' : 'inherit',
                              marginLeft: '-2px'
                            }} 
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles(), cursor: 'pointer' }}
                       onClick={() => {
                        const newOrder = workspacesSortBy === 'created_at' && workspacesSortOrder === 'asc' ? 'desc' : 'asc';
                        setWorkspacesSortBy('created_at');
                        setWorkspacesSortOrder(newOrder);
                        fetchWorkspaces(workspacesPage, workspacesPageSize, workspacesSearch, 'created_at', newOrder);
                      }}
                    >
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Created On
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <ArrowUpward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'created_at' && workspacesSortOrder === 'asc' ? 1 : 0.3,
                              color: workspacesSortBy === 'created_at' && workspacesSortOrder === 'asc' ? 'primary.main' : 'inherit'
                            }} 
                          />
                          <ArrowDownward 
                            fontSize="inherit" 
                            sx={{ 
                              fontSize: '18px', 
                              opacity: workspacesSortBy === 'created_at' && workspacesSortOrder === 'desc' ? 1 : 0.3,
                              color: workspacesSortBy === 'created_at' && workspacesSortOrder === 'desc' ? 'primary.main' : 'inherit',
                              marginLeft: '-2px'
                            }} 
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workspaces.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {workspacesSearch ? 'No workspaces found matching your search' : 'No workspaces found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    workspaces.map((workspace) => (
                      <TableRow key={workspace.id} hover sx={getTableRowStyles()}>
                        <TableCell sx={getTableCellStyles()}>
                          <ExpandableText text={workspace.name} limit={30} />
                        </TableCell>
                        <TableCell sx={getTableCellStyles()}>
                          <ExpandableText text={workspace.description} limit={50} />
                        </TableCell>
                        <TableCell sx={getTableCellStyles()}>
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            {workspace.channel_count}
                          </Typography>
                        </TableCell>
                        <TableCell sx={getTableCellStyles()}>
                          {formatDisplayDateTime(workspace.created_at)}
                        </TableCell>
                        <TableCell sx={getTableCellStyles()}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" color="primary" title="Edit" onClick={() => handleEditWorkspaceClick(workspace)}>
                              <Edit />
                            </IconButton>
                            {workspace.channel_count === 0 && (
                              <IconButton size="small" color="error" title="Delete" onClick={() => handleDeleteWorkspaceClick(workspace)}>
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

           {/* Pagination */}
            {!workspacesLoading && !workspacesError && workspaces.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mt: 2, 
                px: 2,
                py: 2,
                borderTop: '1px solid #e0e0e0',
                backgroundColor: 'background.paper'
              }}>
                 <Typography variant="body2" color="text.secondary">
                  {workspacesTotal > workspacesPageSize 
                    ? `Showing ${((workspacesPage - 1) * workspacesPageSize) + 1} to ${Math.min(workspacesPage * workspacesPageSize, workspacesTotal)} of ${workspacesTotal} workspaces`
                    : `Showing ${workspacesTotal} of ${workspacesTotal} workspaces`
                  }
                </Typography>
                
                {workspacesTotal > workspacesPageSize && (
                  <Pagination
                    count={Math.ceil(workspacesTotal / workspacesPageSize)}
                    page={workspacesPage}
                    onChange={(e, page) => {
                      setWorkspacesPage(page);
                      fetchWorkspaces(page, workspacesPageSize, workspacesSearch, workspacesSortBy, workspacesSortOrder);
                    }}
                    color="primary"
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          color: 'white'
                        }
                      }
                    }}
                  />
                )}
              </Box>
            )}
        </Box>
      </TabPanel>

      {/* Manage Chat Service Tab - only when VITE_DISABLE_SUBSCRIPTION is true */}
      {DISABLE_SUBSCRIPTION && (
        <TabPanel value={tabValue} index={chatServiceTabIndex}>
          <Box sx={{ py: 2 }}>
            <ChatService
              companyId={getCompanyId()}
              mode="admin"
            />
          </Box>
        </TabPanel>
      )}

       {/* Delete User Confirmation Modal */}
       <Dialog
         open={deleteUserModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') {
             return; // Prevent closing when clicking outside
           }
           handleDeleteUserModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: 3,
           }
         }}
       >
         <DialogTitle sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           justifyContent: 'space-between',
           pb: 1
         }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Delete sx={{ color: 'error.main', fontSize: 24 }} />
             <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
               Delete User
             </Typography>
           </Box>
           <IconButton
             onClick={handleDeleteUserModalClose}
             disabled={isDeletingUser}
             sx={{ color: 'error.main' }}
           >
             <CloseIcon />
           </IconButton>
         </DialogTitle>
         
         <DialogContent sx={{ pt: 1 }}>
           {userToDelete && (
             <Typography variant="body1" sx={{ mb: 2 }}>
               Are you sure you want to delete the user{' '}
               <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                 {userToDelete.name}
               </Box>
               {' '}({userToDelete.email_id})?
             </Typography>
           )}
           <Typography variant="body2" color="text.secondary">
             This action cannot be undone. The user will be permanently removed from the system.
           </Typography>
         </DialogContent>
         
         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button
             onClick={handleDeleteUserModalClose}
             disabled={isDeletingUser}
             sx={{ 
               color: 'text.secondary',
               '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
             }}
           >
             Cancel
           </Button>
           <Button
             onClick={handleDeleteUserConfirm}
             disabled={isDeletingUser}
             variant="contained"
             color="error"
             sx={{ 
               backgroundColor: 'error.main',
               '&:hover': { backgroundColor: 'error.dark' },
               '&:disabled': { backgroundColor: 'error.light' }
             }}
             startIcon={isDeletingUser ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isDeletingUser ? 'Deleting...' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Edit Workspace Modal */}
       <Dialog
         open={editWorkspaceModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') return;
           handleEditWorkspaceModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{ sx: { borderRadius: 2, boxShadow: 3 } }}
       >
         <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
           <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Edit Workspace</Typography>
           <IconButton onClick={handleEditWorkspaceModalClose} disabled={isUpdatingWorkspace} sx={{ color: 'text.secondary' }}>
             <CloseIcon />
           </IconButton>
         </DialogTitle>
         <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Name */}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>Workspace Name</Typography>
                <TextField
                  fullWidth
                  value={editWorkspaceFormData.name}
                  onChange={(e) => setEditWorkspaceFormData({ ...editWorkspaceFormData, name: e.target.value })}
                  size="small"
                  placeholder="Enter workspace name"
                />
              </Box>
              {/* Description */}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>Description</Typography>
                <TextField
                  fullWidth
                  value={editWorkspaceFormData.description}
                  onChange={(e) => setEditWorkspaceFormData({ ...editWorkspaceFormData, description: e.target.value })}
                  size="small"
                  multiline
                  rows={3}
                  placeholder="Enter workspace description"
                />
              </Box>
            </Box>
         </DialogContent>
         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button onClick={handleEditWorkspaceModalClose} disabled={isUpdatingWorkspace} sx={{ color: 'text.secondary' }}>Cancel</Button>
           <Button
             onClick={handleUpdateWorkspace}
             disabled={isUpdatingWorkspace || !editWorkspaceFormData.name.trim()}
             variant="contained"
             color="primary"
             startIcon={isUpdatingWorkspace ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isUpdatingWorkspace ? 'Updating...' : 'Update'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Delete Workspace Confirmation Modal */}
       <Dialog
         open={deleteWorkspaceModalOpen}
         onClose={(event, reason) => {
           if (reason === 'backdropClick') return;
           handleDeleteWorkspaceModalClose();
         }}
         maxWidth="sm"
         fullWidth
         disableEscapeKeyDown
         PaperProps={{ sx: { borderRadius: 2, boxShadow: 3 } }}
       >
         <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Delete sx={{ color: 'error.main', fontSize: 24 }} />
             <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Delete Workspace</Typography>
           </Box>
           <IconButton onClick={handleDeleteWorkspaceModalClose} disabled={isDeletingWorkspace} sx={{ color: 'error.main' }}>
             <CloseIcon />
           </IconButton>
         </DialogTitle>
         <DialogContent sx={{ pt: 1 }}>
           {workspaceToDelete && (
             <Typography variant="body1" sx={{ mb: 2 }}>
               Are you sure you want to delete the workspace{' '}
               <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                 {workspaceToDelete.name}
               </Box>
               ?
             </Typography>
           )}
           <Typography variant="body2" color="text.secondary">
             This action cannot be undone. All channels and data associated with this workspace will be permanently removed.
           </Typography>
         </DialogContent>
         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button onClick={handleDeleteWorkspaceModalClose} disabled={isDeletingWorkspace} sx={{ color: 'text.secondary' }}>Cancel</Button>
           <Button
             onClick={handleDeleteWorkspaceConfirm}
             disabled={isDeletingWorkspace}
             variant="contained"
             color="error"
             startIcon={isDeletingWorkspace ? <CircularProgress size={16} color="inherit" /> : null}
           >
             {isDeletingWorkspace ? 'Deleting...' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Add User Modal */}
       <AddUserModal
         open={addUserModalOpen}
         onClose={handleAddUserModalClose}
         companyId={getCompanyId()}
         onSuccess={() => fetchUsers(1, pageSize, usersSearch, usersRoleFilter, usersStatusFilter)}
       />



        {/* Delete Pending Invitation Modal */}
        <Dialog
          open={deletePendingInvitationModalOpen}
          onClose={handleDeletePendingInvitationModalClose}
          aria-labelledby="delete-invitation-dialog-title"
        >
          <DialogTitle
           id="delete-invitation-dialog-title"
           sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Delete sx={{ color: 'error.main', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Delete Invitation
              </Typography>
            </Box>
            <IconButton
              onClick={handleDeletePendingInvitationModalClose}
              disabled={isDeletingPendingInvitation}
              sx={{ color: 'error.main' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 1 }}>
            {pendingInvitationToDelete && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete the invitation for{' '}
                <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {pendingInvitationToDelete.email}
                </Box>
                ?
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The invitation will be permanently removed.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleDeletePendingInvitationModalClose}
              disabled={isDeletingPendingInvitation}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePendingInvitationConfirm}
              disabled={isDeletingPendingInvitation}
              variant="contained"
              color="error"
              sx={{ 
                backgroundColor: 'error.main',
                '&:hover': { backgroundColor: 'error.dark' },
                '&:disabled': { backgroundColor: 'error.light' }
              }}
              startIcon={isDeletingPendingInvitation ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isDeletingPendingInvitation ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

      {/* Plans Modal */}
      <PlansModal
        open={plansModalOpen}
        onClose={() => setPlansModalOpen(false)}
        onPlanSelect={handlePlanSelect}
        currentPlanId={
          subscriptionData?.planName
            ? availablePlans.find((p) => p.name === subscriptionData.planName)?.id
            : undefined
        }
        plans={availablePlans}
        autoFetch={false}
        showCurrentPlanBadge={true}
        gridColumns={{ xs: 1, md: 2, lg: 3 }}
        subscriptionId={getZohoSubscriptionId() || undefined}
        platformName={PLATFORM_NAME}
        onAfterMySubscriptionSynced={
          DISABLE_SUBSCRIPTION
            ? undefined
            : async () => {
                await Promise.all([fetchInvoiceDetails(), fetchSubscriptionData()]);
              }
        }
      />

      {/* View Invoice Modal - shows invoice (PDF) when invoice number is clicked */}
      <ViewInvoiceModal
        open={viewInvoiceModalOpen}
        onClose={() => {
          setViewInvoiceModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoiceId={selectedInvoice?.invoice_id ?? ''}
        invoiceNumber={selectedInvoice?.number ?? ''}
        platformName={PLATFORM_NAME}
      />
     </Box>
   );
 };

export default AdminHub; 
